import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, ShieldCheck, Globe2, FileLock2 } from 'lucide-react';
import { PropertyCard } from '../components/PropertyCard';
import { HOME } from '../constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = '/sovereign-admiral.png';
const SHIELD_IMG = '/sovereign-shield.png';
const BRIDGE_IMG = '/sovereign-bridge.png';

export default function Home() {
  const [properties, setProperties] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/properties`).then((r) => setProperties(r.data.slice(0, 4))).catch(() => {});
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO — LIVE FREE · Sovereign Relocation Hub */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="Live Free — Sovereign Relocation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/30" />
        <div className="relative z-10 px-6 md:px-12 lg:px-24 pb-24 md:pb-32 w-full">
          <div className="max-w-5xl">
            <div className="flex items-center gap-4 mb-8 fade-up">
              <span className="gold-line"></span>
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">
                EIGOO · Sovereign Relocation Hub · v1.0
              </span>
            </div>
            <h1 data-testid="hero-headline" className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extralight leading-[0.95] tracking-tight mb-6 fade-up fade-up-delay-1">
              <span className="font-display italic text-[#D4AF37]">Live Free.</span><br />
              Leave the system.<br />
              Land somewhere <span className="text-[#D4AF37]">sovereign.</span>
            </h1>
            <p className="text-base md:text-lg text-[#A3A3A3] max-w-2xl leading-relaxed font-light mb-12 fade-up fade-up-delay-2">
              A dual-corridor relocation engine for citizens of conscience.
              <span className="text-[#F5F5F5]"> UK Relocation Bridge </span> for Britons exiting the
              tax-and-surveillance net. <span className="text-[#F5F5F5]"> Nigeria Bridge </span>
              for diaspora capital coming home to Lagos, Abuja, and Banana Island.
              Identity-anchored. Off-market. Discreet.
            </p>
            <div className="flex flex-wrap items-center gap-6 fade-up fade-up-delay-3">
              <Link to="/relocation" data-testid="hero-relocation" className="btn-sovereign">
                Begin Your Relocation <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
              <Link
                to="/affiliates"
                data-testid="hero-affiliates"
                className="text-[11px] tracking-[0.3em] uppercase text-[#F5F5F5] hover:text-[#D4AF37] transition-colors flex items-center gap-2"
              >
                Our Vetted Partners <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DUAL BRIDGE — UK Relocation + Nigeria Bridge */}
      <section data-testid="dual-bridge-section" className="bg-[#0A0A0A] border-y border-[#D4AF37]/15">
        <div className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
          <div className="text-center mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Two Bridges · One Sovereign</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight leading-tight tracking-tight max-w-4xl mx-auto">
              Where do you want to <span className="font-display italic text-[#D4AF37]">live free?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* UK Relocation Bridge */}
            <div data-testid="uk-bridge-card" className="sov-border bg-[#050505] p-8 md:p-10 flex flex-col group hover:border-[#D4AF37] transition-colors">
              <div className="flex items-baseline justify-between mb-6">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">Bridge I · Outbound</div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3]">🇬🇧 → 🇳🇬 / 🇺🇸</div>
              </div>
              <h3 className="text-3xl md:text-4xl font-extralight tracking-tight mb-3">
                UK <span className="font-display italic text-[#D4AF37]">Relocation Bridge</span>
              </h3>
              <p className="text-sm text-[#A3A3A3] font-light leading-relaxed mb-8">
                For Britons exiting an over-taxed, over-surveilled island. We architect your
                identity transfer, asset bridge, and landing pad in low-friction sovereign
                corridors — Lagos compounds, Maui beachfront, Abuja diplomatic quarter.
              </p>
              <ul className="space-y-3 mb-10 text-sm text-[#F5F5F5] font-light">
                {[
                  'Non-dom exit playbook · capital gains mitigation',
                  'GBP → NGN / USD asset bridge via Currency Bridge',
                  'Off-market property routing in 4 sovereign cities',
                  'Identity-anchored boarding · KYC vault encrypted',
                ].map((row, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#D4AF37] mt-1.5">●</span>
                    <span>{row}</span>
                  </li>
                ))}
              </ul>
              <Link to="/relocation" className="btn-sovereign mt-auto" data-testid="uk-bridge-cta">
                Board the UK Bridge <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>

            {/* Nigeria Bridge */}
            <div data-testid="nigeria-bridge-card" className="sov-border bg-[#050505] p-8 md:p-10 flex flex-col group hover:border-[#D4AF37] transition-colors">
              <div className="flex items-baseline justify-between mb-6">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">Bridge II · Inbound</div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#A3A3A3]">🌍 → 🇳🇬</div>
              </div>
              <h3 className="text-3xl md:text-4xl font-extralight tracking-tight mb-3">
                Nigeria <span className="font-display italic text-[#D4AF37]">Bridge</span>
              </h3>
              <p className="text-sm text-[#A3A3A3] font-light leading-relaxed mb-8">
                For diaspora returning home with capital. We hold the master register
                of off-market addresses in Ikoyi, Banana Island, Maitama, and Lekki Phase 1 —
                released only to identity-verified sovereign investors.
              </p>
              <ul className="space-y-3 mb-10 text-sm text-[#F5F5F5] font-light">
                {[
                  'Banana Island & Ikoyi waterfront fortresses',
                  'Maitama diplomatic compounds (Abuja)',
                  'Lineage Law document verification',
                  'AI Valuation Shield · sovereign-grade appraisal',
                ].map((row, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-[#D4AF37] mt-1.5">●</span>
                    <span>{row}</span>
                  </li>
                ))}
              </ul>
              <Link to="/vault" className="btn-sovereign mt-auto" data-testid="nigeria-bridge-cta">
                Enter the Nigeria Vault <ArrowUpRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FOUR PILLARS — The Sovereign Relocation Fortress */}
      <section data-testid="four-pillars-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32 bg-[#050505]">
        <div className="text-center mb-16">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-4">The Sovereign Relocation Fortress</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight leading-tight tracking-tight max-w-4xl mx-auto">
            Four pillars. <span className="font-display italic text-[#D4AF37]">One sovereign exit.</span>
          </h2>
          <p className="text-base text-[#A3A3A3] mt-6 max-w-2xl mx-auto font-light leading-relaxed">
            Total life-management for the relocating sovereign. Tap any pillar to enter that command deck.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {[
            { to: '/relocation',  label: 'Relocation',  num: '01', tagline: 'Visa · Document Vault · Milestone Tracker', testId: 'pillar-card-relocation' },
            { to: '/real-estate', label: 'Real Estate', num: '02', tagline: 'Property Recon · Referral Hub · Valuation Shield', testId: 'pillar-card-real-estate' },
            { to: '/finance',     label: 'Finance',     num: '03', tagline: 'UK · Global Banking · Currency Armor', testId: 'pillar-card-finance' },
            { to: '/logistics',   label: 'Logistics',   num: '04', tagline: 'Shipping · Cars · Furniture · Sovereign Mail', testId: 'pillar-card-logistics' },
          ].map((p) => (
            <Link
              key={p.to}
              to={p.to}
              data-testid={p.testId}
              className="sov-border bg-[#0A0A0A] p-8 md:p-10 flex flex-col group hover:bg-[#D4AF37]/5 hover:border-[#D4AF37] transition-all"
            >
              <div className="flex items-baseline justify-between mb-8">
                <div className="font-display text-5xl text-[#D4AF37]/30 group-hover:text-[#D4AF37]/60 transition-colors">{p.num}</div>
                <ArrowUpRight size={18} strokeWidth={1.5} className="text-[#A3A3A3] group-hover:text-[#D4AF37] group-hover:rotate-12 transition-all" />
              </div>
              <h3 className="text-3xl md:text-4xl font-extralight tracking-tight mb-3">{p.label}</h3>
              <p className="text-xs text-[#A3A3A3] font-light tracking-wide leading-relaxed">{p.tagline}</p>
              <div className="mt-auto pt-8 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                Enter Pillar →
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* STATS BAR */}
      {stats && (
        <section className="border-b border-[#D4AF37]/15 bg-[#050505]">
          <div className="px-6 md:px-12 lg:px-24 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">AUM Curated</div>
              <div className="metric-number text-4xl md:text-5xl">${(stats.total_aum_usd / 1_000_000).toFixed(0)}M+</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">Sovereign Listings</div>
              <div className="metric-number text-4xl md:text-5xl">{String(stats.total_properties).padStart(2, '0')}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">Nigeria Bridge</div>
              <div className="metric-number text-4xl md:text-5xl">{stats.nigeria_listings}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">Pacific Corridor</div>
              <div className="metric-number text-4xl md:text-5xl">{stats.usa_listings}</div>
            </div>
          </div>
        </section>
      )}

      {/* THE VAULT PREVIEW */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 mb-16">
          <div className="md:col-span-5">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">III · The Vault</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight leading-tight tracking-tight">
              Addresses that<br />
              <span className="font-display italic text-[#D4AF37]">do not trade publicly.</span>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
              A curated reserve of trans-atlantic addresses — from Banana Island&apos;s waterfront fortresses
              to the lava-cliff sanctuaries of Wailea. Each property is framed in 4K, vetted by lineage,
              and released only to identity-verified sovereigns.
            </p>
          </div>
        </div>

        {properties.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            <div className="md:col-span-7">
              {properties[0] && <PropertyCard property={properties[0]} size="large" />}
            </div>
            <div className="md:col-span-5 space-y-6 md:space-y-8">
              {properties.slice(1, 3).map((p) => (
                <PropertyCard key={p.id} property={p} />
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 flex justify-end">
          <Link to="/vault" className="btn-sovereign">
            Survey All Assets <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* AI VALUATION SHIELD */}
      <section className="bg-[#0A0A0A] border-y border-[#D4AF37]/15">
        <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">II · Valuation Shield</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight leading-tight tracking-tight mb-8">
              An AI sentinel<br />
              <span className="font-display italic text-[#D4AF37]">guarding your appraisal.</span>
            </h2>
            <p className="text-base text-[#A3A3A3] leading-relaxed font-light mb-10 max-w-lg">
              Engineered for trans-atlantic appraisal. Feed it a Lagos triplex
              or a Maui oceanfront — receive a sovereign-grade valuation in seconds,
              with corridor analysis, risk flags, and a recommended posture.
            </p>
            <div className="space-y-5 mb-10">
              {[
                { icon: ShieldCheck, label: 'Bridge-aware market summary' },
                { icon: FileLock2, label: 'Sovereign-grade risk + upside flags' },
                { icon: Globe2, label: 'Nigeria-to-Hawaii capital corridor logic' },
              ].map((row, i) => (
                <div key={i} className="flex items-center gap-4">
                  <row.icon size={18} className="text-[#D4AF37]" strokeWidth={1} />
                  <span className="text-sm text-[#F5F5F5] tracking-wide font-light">{row.label}</span>
                </div>
              ))}
            </div>
            <Link to="/valuation" data-testid={HOME.heroValuation + '-cta'} className="btn-sovereign">
              Engage The Shield <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 relative">
            <div className="absolute -inset-4 border border-[#D4AF37]/15 pointer-events-none"></div>
            <img src={SHIELD_IMG} alt="AI Valuation Shield" className="w-full h-auto object-cover" />
          </div>
        </div>
      </section>

      {/* GLOBAL BRIDGE */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-6 relative">
            <img src={BRIDGE_IMG} alt="Global Bridge" className="w-full h-auto" />
          </div>
          <div className="md:col-span-6">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">III · Global Bridge</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight leading-tight tracking-tight mb-8">
              Mercy commands the<br />
              <span className="font-display italic text-[#D4AF37]">Nigeria-to-US pipeline.</span>
            </h2>
            <p className="text-base text-[#A3A3A3] leading-relaxed font-light mb-8">
              The diaspora capital ladder runs through Lagos, lands in Honolulu, and consolidates in heritage.
              Sovereign Heritage Command is the only vessel built to navigate it — under one admiral.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-10 border-t border-b border-[#D4AF37]/20 py-8">
              {[
                { v: '7000+', l: 'Diaspora Miles' },
                { v: '11hrs', l: 'Capital Transit' },
                { v: '1', l: 'Commander' },
              ].map((s, i) => (
                <div key={i}>
                  <div className="metric-number text-2xl md:text-3xl mb-1">{s.v}</div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">{s.l}</div>
                </div>
              ))}
            </div>
            <Link to="/global-bridge" className="btn-sovereign">
              Cross The Bridge <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* BOARDING CTA */}
      <section className="bg-[#0A0A0A] border-t border-[#D4AF37]/15 relative overflow-hidden">
        <div className="px-6 md:px-12 lg:px-24 py-24 md:py-32 relative z-10">
          <div className="max-w-4xl">
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">IV · Boarding Application</div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-extralight leading-tight tracking-tight mb-10">
              Admission is<br />
              <span className="font-display italic text-[#D4AF37]">identity-anchored.</span>
            </h2>
            <p className="text-base md:text-lg text-[#A3A3A3] leading-relaxed font-light mb-12 max-w-2xl">
              The boarding process is reserved for high-net-worth investors. Submit your sovereign credentials,
              capital posture, and target corridor. Approvals are issued under Mercy's direct seal.
            </p>
            <Link to="/boarding" data-testid={HOME.heroBoarding} className="btn-sovereign">
              Submit Boarding Credentials <ArrowUpRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
